import React from "react";
import {
  COULEUR,
  LARGEUR,
  Figure,
  PhraseAbsence,
  bornes,
  echelle,
  estLibelle,
  estMoment,
  estNombre,
  formatNombre,
  libelleCourt,
  libelleMoment,
  ticksSansCollision,
} from "./_commun";

// Superposition — deux séries comparées l'une à l'autre.
//
// Ce qu'elle apprend (registre) : « où deux séries se séparent — cette année
// contre l'an dernier, le prix contre le recommandé ». Rien ne superpose deux
// périodes ni deux sources de prix dans l'application. Ici : deux courbes sur
// les mêmes instants, et le point où elles s'écartent LE PLUS est marqué et
// chiffré.
//
// ══ FORME DES DONNÉES ═══════════════════════════════════════════════════════
//
//   {
//     unite?:  "%" | "€" | string,
//     serie_a: { label: "2026", points: [{ t: "2026-01", v: 62 }, …] },
//     serie_b: { label: "2025", points: [{ t: "2026-01", v: 58 }, …] },
//   }
//
// Les instants `t` sont alignés par ÉGALITÉ STRICTE : seuls les `t` présents
// dans les deux séries sont dessinés (ordre de serie_a). Il en faut ≥ 2. Un
// point sans jumeau n'est PAS interpolé — il est ignoré, et si cela laisse
// moins de deux instants communs, la phrase le dit.

const H = 200;
const MARGE = { gauche: 46, droite: 14, haut: 34, bas: 26 };

function validerSerie(s) {
  return (
    s &&
    typeof s === "object" &&
    estLibelle(s.label) &&
    Array.isArray(s.points) &&
    s.points.every(
      (p) => p && typeof p === "object" && estMoment(p.t) && estNombre(p.v),
    )
  );
}

function validerSuperposition(d) {
  if (!d || typeof d !== "object")
    return { erreur: "pas de données sur cette période" };
  const { serie_a: a, serie_b: b } = d;
  if (!validerSerie(a) || !validerSerie(b))
    return {
      erreur: "données incomplètes — une des deux séries est vide ou malformée",
    };
  if (a.points.length === 0 || b.points.length === 0)
    return { erreur: "pas de données sur cette période" };
  const parT = new Map(b.points.map((p) => [p.t, p.v]));
  const communs = a.points
    .filter((p) => parT.has(p.t))
    .map((p) => ({ t: p.t, va: p.v, vb: parT.get(p.t) }));
  if (communs.length < 2)
    return {
      erreur:
        "les deux séries n'ont pas assez d'instants en commun pour être comparées",
    };
  return { a, b, communs, unite: d.unite };
}

export default function Superposition({ titre, donnees }) {
  const v = validerSuperposition(donnees);
  if (v.erreur) return <PhraseAbsence titre={titre} texte={v.erreur} />;
  const { a, b, communs, unite } = v;

  const n = communs.length;
  const x = (i) =>
    MARGE.gauche + (i / (n - 1)) * (LARGEUR - MARGE.gauche - MARGE.droite);
  const [yMin, yMax] = bornes(communs.flatMap((c) => [c.va, c.vb]));
  const y = echelle(yMin, yMax, H - MARGE.bas, MARGE.haut);
  const trace = (cle) =>
    communs
      .map(
        (c, i) =>
          `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(c[cle]).toFixed(1)}`,
      )
      .join(" ");

  // Le point de plus grande séparation.
  let idxMax = 0;
  for (let i = 1; i < n; i++) {
    if (
      Math.abs(communs[i].va - communs[i].vb) >
      Math.abs(communs[idxMax].va - communs[idxMax].vb)
    )
      idxMax = i;
  }
  const sep = communs[idxMax];
  const ecartMax = Math.abs(sep.va - sep.vb);

  const conclusion =
    ecartMax === 0
      ? `${titre} : ${a.label} et ${b.label} sont confondues sur toute la période`
      : `${titre} : ${a.label} et ${b.label} se séparent le plus en ${libelleMoment(sep.t)} (${formatNombre(sep.va, unite)} contre ${formatNombre(sep.vb, unite)})`;

  const ticks = ticksSansCollision([0, n - 1, idxMax], x, idxMax);
  const vMin = Math.min(...communs.flatMap((c) => [c.va, c.vb]));
  const vMax = Math.max(...communs.flatMap((c) => [c.va, c.vb]));

  return (
    <Figure
      forme="superposition"
      titre={titre}
      conclusion={conclusion}
      hauteur={H}
    >
      {/* Légende : quelle courbe est quoi. */}
      <g fontSize="10.5" fill={COULEUR.navyD}>
        <line
          x1={MARGE.gauche}
          x2={MARGE.gauche + 18}
          y1={12}
          y2={12}
          stroke={COULEUR.navy}
          strokeWidth="2"
        />
        <text x={MARGE.gauche + 23} y={16}>
          {a.label}
        </text>
        <line
          x1={MARGE.gauche + 90}
          x2={MARGE.gauche + 108}
          y1={12}
          y2={12}
          stroke={COULEUR.ciel}
          strokeWidth="2"
          strokeDasharray="4 2"
        />
        <text x={MARGE.gauche + 113} y={16}>
          {b.label}
        </text>
      </g>

      {/* Repères de valeur. */}
      <text
        x={MARGE.gauche - 6}
        y={y(vMax) + 4}
        textAnchor="end"
        fontSize="10"
        fill={COULEUR.gris}
      >
        {formatNombre(vMax, unite)}
      </text>
      <text
        x={MARGE.gauche - 6}
        y={y(vMin) + 4}
        textAnchor="end"
        fontSize="10"
        fill={COULEUR.gris}
      >
        {formatNombre(vMin, unite)}
      </text>

      {/* Les deux courbes : A pleine en navy, B pointillée en ciel. */}
      <path
        d={trace("vb")}
        fill="none"
        stroke={COULEUR.ciel}
        strokeWidth="2"
        strokeDasharray="4 2"
        strokeLinejoin="round"
      />
      <path
        d={trace("va")}
        fill="none"
        stroke={COULEUR.navy}
        strokeWidth="2"
        strokeLinejoin="round"
      />

      {/* Là où elles se séparent le plus : un trait entre les deux, chiffré. */}
      {ecartMax > 0 ? (
        <g data-testid="superposition-separation">
          <line
            x1={x(idxMax)}
            x2={x(idxMax)}
            y1={y(sep.va)}
            y2={y(sep.vb)}
            stroke={COULEUR.terra}
            strokeWidth="2"
          />
          <circle cx={x(idxMax)} cy={y(sep.va)} r="3.5" fill={COULEUR.terra} />
          <circle cx={x(idxMax)} cy={y(sep.vb)} r="3.5" fill={COULEUR.terra} />
          <text
            x={Math.min(
              Math.max(x(idxMax), MARGE.gauche + 40),
              LARGEUR - MARGE.droite - 40,
            )}
            y={MARGE.haut - 8}
            textAnchor="middle"
            fontSize="11.5"
            fontWeight="600"
            fill={COULEUR.terra}
          >
            {`${formatNombre(sep.va, unite)} contre ${formatNombre(sep.vb, unite)}`}
          </text>
        </g>
      ) : null}

      {/* L'axe du temps. */}
      {ticks.map((i) => (
        <text
          key={i}
          data-testid="axe-temps"
          x={x(i)}
          y={H - 8}
          textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
          fontSize="10"
          fill={i === idxMax && ecartMax > 0 ? COULEUR.terra : COULEUR.gris}
          fontWeight={i === idxMax && ecartMax > 0 ? "600" : "400"}
        >
          {libelleCourt(communs[i].t)}
        </text>
      ))}
    </Figure>
  );
}
